import type {History} from 'history';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Illustration} from 'shells/illustrations/illustration';
import {Stack} from 'shells/layout/stack/stack';
import {
    NavButtons,
    RightFrame,
    ModalMain,
    ModalMainIllustrationContainer,
} from 'shells/modal/step-modal/layout';
import {ModalBody} from 'shells/typography/modal-body';
import {ModalHeader} from 'shells/typography/modal-header';
import {ModalTitle} from 'shells/typography/modal-title';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {GetSessionUser_loggedInUser} from 'nutshell-graphql-types';

import {addNotification} from '../../../notifications/index';

import {useSetKixieIntegrationIsEnabled} from './graphql/hooks/use-set-kixie-integration-is-enabled';

type Props = {
    integrationType: ExternalServiceType | null | undefined;
    user: GetSessionUser_loggedInUser | null;
    locationSearch: string;
    nextPage: string;
    history: History;
    onCancel: () => void;
};

export function GettingStarted(props: Props) {
    const nextPage = props.nextPage;

    const {setKixieIntegrationIsEnabled, isLoading: isLoadingKixie} =
        useSetKixieIntegrationIsEnabled();

    const handleStart = () => {
        // Kixie is an instance-wide integration, so we need to check if it's enabled for the instance
        // If it is, then the user only has to configure their activity types.
        if (props.integrationType === 'KIXIE') {
            const isEnabled = props.user?.phoneIntegrations.some(
                (integration) => integration.integrationType === 'KIXIE' && integration.integration
            );

            if (isEnabled) {
                setKixieIntegrationIsEnabled(true)
                    .then(() => {
                        props.history.push({
                            pathname: '/connect-phone-integration/configure-activity-types',
                            search: props.locationSearch,
                        });
                    })
                    .catch(() => {
                        addNotification('Failed to enable Kixie integration', {
                            body: 'Please try again.',
                            icon: 'frowny',
                        });
                    });

                return Promise.resolve();
            }
        }

        props.history.push(nextPage);

        return Promise.resolve();
    };

    return (
        <RightFrame>
            <ModalMain>
                <ModalMainIllustrationContainer minHeight={240}>
                    <Illustration src='trial-onboarding/phone-integration-onboarding' width={350} />
                </ModalMainIllustrationContainer>
                <ModalHeader>Get started</ModalHeader>
                <Stack spacing={12}>
                    <div>
                        <ModalTitle lessBottomMargin={true}>Set up and connect calling</ModalTitle>
                        <ModalBody>
                            Choose how to make calls in Nutshell and sync with your phone system,
                            including providers like RingCentral or Dialpad.
                        </ModalBody>
                    </div>
                    <div>
                        <ModalTitle lessBottomMargin={true}>Call from your browser</ModalTitle>
                        <ModalBody>
                            Use Click-to-Call to reach contacts in one click without leaving
                            Nutshell.
                        </ModalBody>
                    </div>
                    <div>
                        <ModalTitle lessBottomMargin={true}>Capture and summarize calls</ModalTitle>
                        <ModalBody>
                            Automatically record, transcribe, import, and summarize all your calls.
                        </ModalBody>
                    </div>
                </Stack>
            </ModalMain>
            <NavButtons>
                <Button variant='text' onClick={props.onCancel}>
                    Cancel
                </Button>
                <SaveButton
                    variant='primary'
                    size='big'
                    onClick={handleStart}
                    buttonText='Start'
                    savingText='Starting...'
                    successText='Started'
                    isSaving={isLoadingKixie}
                />
            </NavButtons>
        </RightFrame>
    );
}
